import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { from, interval, mergeMap, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { VerifiedEmail } from '../../../auth/model/email-verification-token.model';
import { UserRole } from '../../../auth/model/user-role.model';
import { TokenService } from '../../../auth/services/token.service';
import { AppConfig } from '../../../core/services/app-config.service';
import { Store } from '../../../core/services/store.service';
import {
  IChangePasswordForm,
  IEmailVerificationForm,
  ILoginForm,
  IRegistrationForm,
  ISendEmailForm,
  IUserInfo,
  IVerifyCodeForm,
} from '../../interfaces/auth.interface';
import { AuthState } from '../../models/auth/auth-state.model';
import { AuthApiService } from './auth.api.service';
import { ERROR_KEY } from '../../../auth/services/global-error-handler.service';
import { Router } from '@angular/router';
import { DialogService } from '../../../core/services/dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BannerService } from '../../../core/services/banner.service';
import { CsrfStoreService } from '../../../auth/services/csrf-store.service';

const BLOCKED_INFO_SEEN = 'blocked_info_seen';
const HARD_BLOCKED_INFO_SEEN = 'hard_blocked_info_seen';
const VERIFICATION_IN_PROGRESS_INFO_SEEN = 'verification_in_progess_info_seen';

@Injectable({
  providedIn: 'root',
})
export class AuthStoreService extends Store<AuthState> {
  public userInfo: IUserInfo;
  public isAuthenticated: boolean;
  public isDemo = false;
  public isAdmin: boolean;
  public isBlockedInfoSeen: boolean;
  public isHardBlockedInfoSeen: boolean;
  public isVerificationInProgressInfoSeen: boolean;
  private tokenExpired = false;

  private logoutSubject = new Subject<void>();
  logout$ = this.logoutSubject.asObservable();

  private loginSubject = new Subject<void>();
  login$ = this.loginSubject.asObservable();

  constructor(
    private appConfig: AppConfig,
    private authApi: AuthApiService,
    private jwtHelperService: JwtHelperService,
    private tokenService: TokenService,
    private csrfService: CsrfStoreService,
    private router: Router,
    private dialog: DialogService,
    private snackBar: MatSnackBar,
    private bannerService: BannerService,
  ) {
    super(AuthState.NOT_AUTHENTICATED);

    this.refreshState();

    if (this.isAuthenticated) {
      this.setState(AuthState.AUTHENTICATED);
    }

    this.state$.pipe(tap(() => this.refreshState())).subscribe();

    interval(5000)
      .pipe(
        mergeMap(() => from(this.needToRefresh())),
        filter((needToRefresh) => needToRefresh),
        switchMap(() => this.refreshToken()),
      )
      .subscribe();
  }

  public setBlockedInfoSeen(): void {
    localStorage.setItem(BLOCKED_INFO_SEEN, 'true');
    this.isBlockedInfoSeen = true;
  }

  public setHardBlockedInfoSeen(): void {
    localStorage.setItem(HARD_BLOCKED_INFO_SEEN, 'true');
    this.isHardBlockedInfoSeen = true;
  }

  public setVerificationInProgressInfoSeen(): void {
    localStorage.setItem(VERIFICATION_IN_PROGRESS_INFO_SEEN, 'true');
    this.isVerificationInProgressInfoSeen = true;
  }

  public login(data: ILoginForm): Observable<any> {
    return this.authApi.login(data).pipe(
      tap(() => {
        this.clearLocaleStorage();
        this.tokenService.rememberMe = data.rememberMe;
        this.setState(AuthState.AUTHENTICATED);
        this.loginSubject.next();
      }),
    );
  }

  private clearLocaleStorage() {
    localStorage.removeItem(ERROR_KEY);
    this.tokenService.clearLocalStorageSeviceItems();
    this.csrfService.clear();
  }

  public register(data: IRegistrationForm): Observable<any> {
    return this.authApi.register(data).pipe(
      tap(() => {
        this.clearLocaleStorage();
        this.setState(AuthState.REGISTERED);
      }),
    );
  }

  public verifyEmail(data: IEmailVerificationForm): Observable<VerifiedEmail> {
    return this.authApi.verifyEmail(data).pipe(
      tap(() => {
        this.setState(AuthState.AUTHENTICATED);
      }),
    );
  }

  public resendEmailVerification(): Observable<any> {
    return this.authApi.resendEmailVerification(this.userInfo);
  }

  public discontinueEmailVerification(path: string): Observable<any> {
    return this.authApi.discontinueEmailVerification(path);
  }

  public sendEmail(data: ISendEmailForm): Observable<any> {
    return this.authApi.sendEmail(data);
  }

  public verifyCode(data: IVerifyCodeForm): Observable<any> {
    return this.authApi.verifyCode(data);
  }

  public changePassword(data: IChangePasswordForm): Observable<any> {
    return this.authApi.changePassword(data);
  }

  public logout(removed?: boolean): Observable<any> {
    return this.authApi.logout().pipe(
      tap(() => {
        this.clearLocaleStorage();
        this.snackBar.dismiss();
        this.logoutSubject.next();
      }),
      catchError((err, caught) => {
        this.clearLocaleStorage();
        this.snackBar.dismiss();
        this.logoutSubject.next()
        return of()
        }
      ),
      finalize(() => {
        this.tokenService.clearLocalStorageItems();
        this.setState(AuthState.NOT_AUTHENTICATED);
        this.checkAndRedirect(removed);
        this.bannerService.hideBanner();
      }),
    );
  }

  private refreshState() {
    this.userInfo = this.jwtHelperService.decodeToken() as IUserInfo;
    this.isAuthenticated = !this.jwtHelperService.isTokenExpired() && this.userInfo.email_verified;
    this.isAdmin = this.userInfo && this.userInfo.roles && this.userInfo.roles.includes(UserRole.ROLE_ADMIN);
    this.isBlockedInfoSeen =
      localStorage.getItem(BLOCKED_INFO_SEEN) && localStorage.getItem(BLOCKED_INFO_SEEN) === 'true';
    this.isHardBlockedInfoSeen = localStorage.getItem(HARD_BLOCKED_INFO_SEEN) === 'true';
    this.isVerificationInProgressInfoSeen = localStorage.getItem(VERIFICATION_IN_PROGRESS_INFO_SEEN) === 'true';
  }

  private async checkAndRedirect(removed?: boolean) {
    // @ts-ignore
    if (typeof mobileLogoutFun === 'function') {
      try {
        // @ts-ignore
        await mobileLogoutFun().then((loggedOut) => {
          if (loggedOut) {
            this.navigateToLogin(removed);
          }
        });
      } catch (err) {
        console.log(err);
        this.navigateToLogin(removed);
      }
    } else {
      this.navigateToLogin(removed);
    }
  }

  private navigateToLogin(removed = false) {
    if (removed) {
      this.router.navigateByUrl('/auth/removed');
    } else {
      this.router.navigateByUrl('/auth/login');
    }
  }

  public refreshToken(): Observable<any> {
    return this.authApi.refreshToken().pipe(
      tap(() => this.setState(AuthState.AUTHENTICATED)),
      catchError(() => {
        this.failedRefreshToken();

        return throwError('Refresh failed');
      }),
    );
  }

  public revertImpersonation() {
    if (this.tokenService.isImpersonalization()) {
      this.authApi.revertImpersonation()
        .subscribe();
    }
  }

  public isImpersonalization() {
    return this.tokenService.isImpersonalization();
  }

  public failedRefreshToken() {
    this.logout().subscribe();
    // this.dialog.open(ErrorRefreshingTokenDialogComponent, { closeOnNavigation: false })
    //   .afterClosed().subscribe(() => {
    // });
  }

  public deleteCustomer(): void {
    this.logout();
  }

  public clearData(): void {
    this.tokenService.clearLocalStorageItems();
    this.clearLocaleStorage();

    this.clearBlockedInfoSeen();
  }

  private clearBlockedInfoSeen(): void {
    localStorage.removeItem(BLOCKED_INFO_SEEN);
  }

  get customerId(): string {
    return this.userInfo.customer_id;
  }

  get payByLink(): string {
    return this.userInfo.pay_by_link;
  }

  private async needToRefresh(): Promise<boolean> {
    const expToken = await this.jwtHelperService.getTokenExpirationDate();

    if (!expToken || !(expToken instanceof Date)) {
      return false; // Brak daty wygaśnięcia → brak potrzeby odświeżenia
    }

    const expDate = expToken.getTime(); // `getTime()` działa tylko na `Date`
    const currentDate = Date.now(); // Krótsza wersja `new Date().getTime()`

    return expDate - currentDate < 15 * 1000;
  }

}
